import type * as common from '../../common'

import type * as config from './config'
import type * as data from './data'
import type { Type } from './type'

type ChartBase = common.Named.Optionally & common.Described.Optionally
/**
 * A chart is a visual representation of data, like bar charts, donut charts,
 * line charts, etc.
 */
export type Chart<TType extends Type = Type> =
  | (TType extends any
      ? ChartBase & {
          /**
           * @see {@link Chart.Type}
           */
          // NOTE: this is not optional because it intereferes with the ability to
          // have a discriminate union (but we add `undefined` member of union
          // below)
          type: TType
          // NOTE: these are all optional so we are forced to handle it
          /**
           * @see {@link data.Of}
           */
          data?: data.Of<TType>
          /**
           * @see {@link config.Of}
           */
          config?: config.Of<TType>
        }
      : never)
  | (ChartBase & { type?: undefined; data?: undefined; config?: undefined })
export namespace Chart {
  /**
   * Collection of {@link Chart}, generally to be displayed in the same widget
   */
  export type Collection<TType extends Type = Type> = TType extends any
    ? Omit<Chart<TType>, 'data'> & {
        /**
         * List of {@link Chart.data}
         */
        data?: readonly data.Of<TType>[]
      }
    : never
  export namespace Collection {
    /**
     * Converts {@link Collection} to a list of {@link Chart}
     */
    export function toCharts<const TCollection extends Collection>(
      collection: TCollection,
    ): TCollection extends Collection<infer TType>
      ? readonly Chart<TType>[]
      : never
    // NOTE: this is technically covered by the above overload, but it makes it
    // simpler for a consumer to explicitly set TType
    export function toCharts<const TType extends Type>(
      collection: Collection<TType>,
    ): readonly Chart<TType>[]
    export function toCharts(collection: Collection): readonly Chart[] {
      return (collection.data ?? []).map(
        // NOTE: because we distribute over `Type`, TypeScript can't verify that
        // the type and data are aligned like they should be. However, we know
        // they should be aligned because of the way `Collection` is typed. So
        // we use `satisfies` with `FlattenUnion` to check that we have the
        // right properties without checking that the properties are aligned
        // with each other.
        data =>
          ({
            ...collection,
            data,
          }) satisfies FlattenUnion<Chart> as Chart,
      )
    }
  }
}
